import type * as SentryNS from '@sentry/react'

import { getLoadedSentry } from './sentryLazy'

// T2028: piškotkova pasica ponuja »Zavrni«, a do te naloge izbire ni bral nihče —
// Sentry je v produkciji ne glede nanjo snemal potek seje ob napaki in pošiljal
// meritve hitrosti (transakcije). Odslej velja:
//
// - dokler izbire ni (`null`) ali po »Zavrni« (`rejected`): brez zapisa seje
//   (Session Replay) in brez sledenja (transakcij). Javljanje napak ostane —
//   brez osebnih podatkov, kot ga opisuje politika zasebnosti (§5, T1869);
// - po »Sprejmi vse« (`accepted`): zapis seje ob napaki in vzorčeno sledenje.
//
// Sprememba izbire učinkuje brez ponovnega nalaganja: sledenje prek
// `tracesSampler`, ki soglasje prebere ob začetku vsake transakcije, zapis seje
// pa z lenim `addIntegration` oziroma `stop()`/`startBuffering()`.
//
// T2899: Sentry se naloži po prvem izrisu (`sentryLazy.ts`), zato ta modul (ki ga
// uvozi piškotkova pasica v vstopnem grafu) Sentryja ne uvozi — bere naloženega.
// Izbira pred nalaganjem se uveljavi ob `init` (`afterSentryInit`).

export type ConsentValue = 'accepted' | 'rejected'

export const CONSENT_STORAGE_KEY = 'gdpr_consent'

// Izbira iz te seje; velja tudi, ko `localStorage` ni dosegljiv.
let memoryConsent: ConsentValue | null = null

/** Prebere izbiro iz pasice; nedosegljiv `localStorage` šteje kot »ni izbire«. */
export function readConsent(): ConsentValue | null {
  if (memoryConsent) return memoryConsent
  try {
    const value = localStorage.getItem(CONSENT_STORAGE_KEY)
    return value === 'accepted' || value === 'rejected' ? value : null
  } catch {
    return null
  }
}

/** Zapis seje in sledenje sta dovoljena samo po izrecnem »Sprejmi vse«. */
export function hasMonitoringConsent(): boolean {
  return readConsent() === 'accepted'
}

/**
 * `tracesSampler` za `Sentry.init`: brez soglasja vrne 0 (transakcija se ne
 * pošlje), s soglasjem podano stopnjo. Bere se ob vsaki transakciji, zato
 * sprememba izbire velja takoj.
 */
export function makeTracesSampler(rate: number): () => number {
  return () => (hasMonitoringConsent() ? rate : 0)
}

type ReplayFactory = () => ReturnType<typeof SentryNS.replayIntegration>

let replayFactory: ReplayFactory | null = null
let replayActive = false

/**
 * Zapis seje se ne poda v `Sentry.init`, ampak se tu registrira tovarna, ki jo
 * `applySentryConsent()` uporabi šele ob soglasju.
 */
export function registerReplayFactory(factory: ReplayFactory | null): void {
  replayFactory = factory
}

/** Uskladi stanje Sentryja s trenutno izbiro v pasici. */
export function applySentryConsent(): void {
  const Sentry = getLoadedSentry()
  if (!Sentry || !Sentry.getClient()) return

  const replay = Sentry.getReplay()

  if (hasMonitoringConsent()) {
    if (!replay) {
      if (!replayFactory) return
      // Integracija ob dodajanju sama začne zapisovati v medpomnilnik
      // (`replaysOnErrorSampleRate`), zato je tu ni treba zagnati.
      Sentry.addIntegration(replayFactory())
      replayActive = true
    } else if (!replayActive) {
      replay.startBuffering()
      replayActive = true
    }
    return
  }

  if (replay && replayActive) {
    void replay.stop()
  }
  replayActive = false
}

/** Zapiše izbiro in jo takoj uveljavi. */
export function setConsent(value: ConsentValue): void {
  memoryConsent = value
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, value)
  } catch {
    // Brez shrambe izbira velja samo do ponovnega nalaganja.
  }
  applySentryConsent()
}

/** Samo za teste: ponastavi stanje modula. */
export function __resetSentryConsentForTests(): void {
  memoryConsent = null
  replayFactory = null
  replayActive = false
}
